import { type ChangeEvent, useEffect, useState } from "react";
import { connect, type ConnectedProps } from "react-redux";
import { Accordion } from "semantic-ui-react";
import environmentConfig from "../../../configuration/environmentConfig";
import NavigationMenuItem from "../navigationMenuItem/NavigationMenuItem";
import ReportingNavigationMenu from "../reportingNavigationMenu/ReportingNavigationMenu";

import { RolePermissions, RouteNames } from "../../../enums";
import { FeatureFlags } from "featureFlags";
import { fetchMostAccessedAccounts as fetchMostAccessedAccountsRedux } from "../../../features/Accounts/MostAccessedAccounts/state/mostAccessedAccountsActions";
import { useHasPermissions } from "../../../features/Application/hooks";
import { useFeatureFlag } from "hooks/useFeatureFlag";
import { Tooltip } from "../../common/tooltip";

import { AccountTypeEnum } from "interfaces/Account";
import { type AppDispatch, type RootState } from "../../../features/Application/globaltypes/redux";
import { bindAction } from "../../../interfaces";

export type TopMenuAccordionProps = {
  activeItems: string[];
  collapsed: boolean;
  fetchMostAccessedAccounts: () => void;
  topLevelItemClick: (path?: string) => void;
} & PropsFromRedux;

const renderNavigationMenuItems = (
  hideNavigation: () => void,
  isExternalLinksEnabled: boolean,
  isScormEnabled: boolean,
) => {
  const items = [
    { label: "Flows", path: "flows" },
    { label: "Events", path: "events" },
    { label: "Communications", path: "communications" },
    { label: "Videos", path: "assets/videos" },
    { label: "Surveys", path: "assets/surveys" },
    { label: "Assessments", path: "assets/assessments" },
    { label: "PDFs", path: "assets/pdfs" },
  ];

  if (isExternalLinksEnabled) {
    items.push({ label: "Links", path: "assets/links" });
  }
  if (isScormEnabled) {
    items.push({ label: "SCORM", path: "assets/scorm" });
  }

  return items.map((item) => (
    <NavigationMenuItem withMobo key={item.path} label={item.label} path={item.path} hideNavigation={hideNavigation} />
  ));
};

type ContentProps = {
  topLevelItemClick: (path?: string) => void;
  collapsed: boolean;
  activeItems: string[];
  toggleNavigation: boolean;
  hideNavigation: () => void;
  isExternalLinksEnabled: boolean;
  isScormEnabled: boolean;
  showDomainManagement?: boolean;
};

function renderContents({
  topLevelItemClick,
  collapsed,
  activeItems,
  toggleNavigation,
  hideNavigation,
  isExternalLinksEnabled,
  isScormEnabled,
  showDomainManagement,
}: ContentProps) {
  return (
    <NavigationMenuItem
      label="Content"
      icon="fa-folder-open"
      className="content-menu-item"
      path={RouteNames.content}
      onClick={topLevelItemClick}
      expanded={!collapsed && activeItems.includes(RouteNames.content)}
      toggleNavigation={toggleNavigation}
    >
      {collapsed && (
        <NavigationMenuItem
          label="Content"
          path="content"
          onClick={topLevelItemClick}
          expanded={activeItems.includes(RouteNames.content)}
          container
        >
          {renderNavigationMenuItems(hideNavigation, isExternalLinksEnabled, isScormEnabled)}
        </NavigationMenuItem>
      )}

      {!collapsed && <NavigationMenuItem withMobo label="Flows" path={"flows"} />}
      {!collapsed && <NavigationMenuItem withMobo label="Events" path={"events"} />}
      {!collapsed && <NavigationMenuItem withMobo label="Communications" path={"communications"} />}
      {!collapsed && <NavigationMenuItem withMobo label="Videos" path={"assets/videos"} />}
      {!collapsed && <NavigationMenuItem withMobo label="Surveys" path={"assets/surveys"} />}
      {!collapsed && <NavigationMenuItem withMobo label="Assessments" path={"assets/assessments"} />}
      {!collapsed && <NavigationMenuItem withMobo label="PDFs" path={"assets/pdfs"} />}
      {!collapsed && isExternalLinksEnabled && <NavigationMenuItem withMobo label="Links" path={"assets/links"} />}
      {!collapsed && isScormEnabled && <NavigationMenuItem withMobo label="SCORM" path={"assets/scorm"} />}

      <NavigationMenuItem
        label="Simulated Phishing"
        onClick={topLevelItemClick}
        path={RouteNames.simulatedPhishing}
        expanded={activeItems.includes(RouteNames.contentSimulatedPhishing)}
        toggleNavigation={toggleNavigation}
      >
        <NavigationMenuItem label="Campaigns" path="campaigns" hideNavigation={hideNavigation} />
        {showDomainManagement && (
          <NavigationMenuItem
            label="Domain Management"
            path={RouteNames.domainManagement}
            hideNavigation={hideNavigation}
          />
        )}
      </NavigationMenuItem>
    </NavigationMenuItem>
  );
}

function renderRestrictedContents({
  topLevelItemClick,
  collapsed,
  activeItems,
  toggleNavigation,
  hideNavigation,
  isExternalLinksEnabled,
  isScormEnabled,
}: ContentProps) {
  return (
    <NavigationMenuItem
      label="Content"
      icon="fa-folder-open"
      className="content-menu-item"
      path={RouteNames.content}
      onClick={topLevelItemClick}
      expanded={!collapsed && activeItems.includes(RouteNames.content)}
      toggleNavigation={toggleNavigation}
    >
      {renderNavigationMenuItems(hideNavigation, isExternalLinksEnabled, isScormEnabled)}
    </NavigationMenuItem>
  );
}

const accountsPermissions = [RolePermissions.AccountsSettingsCreate, RolePermissions.TemplatesManage];

export function TopMenuAccordion({
  activeItems,
  collapsed,
  topLevelItemClick,
  fetchMostAccessedAccounts,
  hasAnyLinkedAccount,
  accountTypeId,
}: TopMenuAccordionProps) {
  const [toggleNavigation, setToggleNavigation] = useState(true);
  const showTemplates = useHasPermissions([RolePermissions.TemplatesView, RolePermissions.TemplatesManage]);
  const showAccounts = useHasPermissions(accountsPermissions) || hasAnyLinkedAccount;
  const isExternalLinksEnabled = useFeatureFlag(FeatureFlags.LinkAssetType);
  const isScormEnabled = useFeatureFlag(FeatureFlags.Scorm);
  const isDomainManagementEnabled = useFeatureFlag(FeatureFlags.DomainManagement);
  const hasDomainManagementPermissions = useHasPermissions([RolePermissions.CampaignsManage]);
  const showDomainManagement =
    accountTypeId === AccountTypeEnum.BSI && hasDomainManagementPermissions && isDomainManagementEnabled;

  useEffect(() => {
    fetchMostAccessedAccounts();
  }, [fetchMostAccessedAccounts]);

  const hideNavigation = () => {
    setToggleNavigation(false);
  };

  const showNavigation = () => {
    setToggleNavigation(true);
  };

  const renderAccounts = () => {
    if (!showAccounts) return null;

    return (
      <NavigationMenuItem
        label="Accounts"
        icon="building"
        path={RouteNames.accounts}
        onClick={topLevelItemClick}
        expanded={activeItems.includes(RouteNames.accounts)}
        toggleNavigation={toggleNavigation}
        container
      >
        {showAccounts && (
          <NavigationMenuItem withMobo label="All" path={RouteNames.linkedAccounts} hideNavigation={hideNavigation} />
        )}

        {showTemplates && (
          <NavigationMenuItem withMobo label="Templates" path={RouteNames.templates} hideNavigation={hideNavigation} />
        )}
      </NavigationMenuItem>
    );
  };

  return (
    <Accordion fluid onMouseOver={showNavigation} className="global-navigation">
      {renderAccounts()}

      <NavigationMenuItem
        label="People"
        icon="fa-user"
        path={RouteNames.people}
        onClick={topLevelItemClick}
        expanded={activeItems.includes(RouteNames.people)}
        toggleNavigation={toggleNavigation}
      >
        <NavigationMenuItem withMobo label="Users" path={"users"} hideNavigation={hideNavigation} />
        <NavigationMenuItem withMobo label="Roles" path={"roles"} hideNavigation={hideNavigation} />
        <NavigationMenuItem withMobo label="Groups" path={"groups"} hideNavigation={hideNavigation} />
      </NavigationMenuItem>
      <ReportingNavigationMenu
        topLevelItemClick={topLevelItemClick}
        expanded={activeItems.includes(RouteNames.reporting)}
        toggleNavigation={toggleNavigation}
        hideNavigation={hideNavigation}
      />

      <Tooltip
        content={"Marketplace"}
        showAlways={collapsed}
        position={"right center"}
        target={
          <NavigationMenuItem
            withMobo
            label="Marketplace"
            icon="fa-shopping-cart"
            path={RouteNames.availablePacks}
            activePath={RouteNames.licensingPacks}
            onClick={topLevelItemClick}
            expanded={activeItems.includes(RouteNames.availablePacks)}
          />
        }
      />

      {useHasPermissions([RolePermissions.CampaignsManage])
        ? renderContents({
            topLevelItemClick,
            collapsed,
            activeItems,
            showDomainManagement,
            toggleNavigation,
            hideNavigation,
            isExternalLinksEnabled,
            isScormEnabled,
          })
        : renderRestrictedContents({
            topLevelItemClick,
            collapsed,
            activeItems,
            toggleNavigation,
            hideNavigation,
            isExternalLinksEnabled,
            isScormEnabled,
          })}

      <Tooltip
        content={"Help"}
        showAlways={collapsed}
        position={"right center"}
        target={
          <NavigationMenuItem
            blur
            label="Help"
            onClick={(e: ChangeEvent<HTMLButtonElement>) => e.currentTarget?.blur()}
            icon="fa-life-ring"
            className="help-menu-item"
            path={environmentConfig.helpCenterUrl}
            external
          />
        }
      />
    </Accordion>
  );
}

const mapStateToProps = (state: RootState) => ({
  accountId: state.userProfile.accountId,
  hasAnyLinkedAccount: state.accounts.mostAccessed.items.length > 0,
  accountTypeId: state.userProfile.accountTypeId,
});

const mapDispatchToProps = (dispatch: AppDispatch) => ({
  fetchMostAccessedAccounts: bindAction(fetchMostAccessedAccountsRedux, dispatch),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(TopMenuAccordion);
export default ConnectedComponent;
